import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWind } from "@fortawesome/sharp-light-svg-icons";
import { Popover } from "@headlessui/react";
import { useState } from "react";
import { defaultAnalyticsFilters } from "../state";

interface DetectionLimitFilterProps {
    value: number;
    onChange: (value: number) => void;
}

export const DetectionLimitFilter = ({
    value,
    onChange,
}: DetectionLimitFilterProps) => {
    const [tempValue, setTempValue] = useState<string>(value.toString());
    const isDefault = defaultAnalyticsFilters.detectionLimit === value;

    // Floating UI setup for popover positioning
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
            offset({
                mainAxis: 3,
                crossAxis: 0,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const displayValue = value === 0 ? "-" : `${value} kg/h`;

    const handleSave = () => {
        const numValue = parseFloat(tempValue);
        if (!Number.isNaN(numValue) && numValue >= 0) {
            onChange(numValue);
        } else {
            setTempValue(value.toString());
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSave();
            // Close popover by blurring
            e.currentTarget.blur();
        }
    };

    return (
        <div ref={refs.setReference}>
            <Popover>
                {({ open, close }) => (
                    <>
                        <Popover.Button
                            className={`
                                flex items-center gap-2 px-3 h-8 text-sm border
                                rounded bg-white hover:bg-ae-gray-100
                                ${isDefault ? "border-ae-gray-250" : "border-ae-blue-550 bg-ae-blue-100"}
                            `}
                            onClick={() => {
                                if (!open) {
                                    setTempValue(value.toString());
                                }
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faWind}
                                className="w-4 text-ae-blue-550"
                            />
                            <div className="text-neutral-600 hidden lg:flex items-center gap-1 whitespace-nowrap">
                                <span className="hidden xl:block">
                                    Emissions
                                </span>
                                <span>
                                    {value > 0 ? `≥ ${displayValue}` : "(all)"}
                                </span>
                            </div>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="w-3"
                            />
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            onKeyUp={(key) => {
                                if (key.code === "Enter") {
                                    close();
                                }
                            }}
                            className="w-64 rounded-md border-2 border-ae-slate-300 drop-shadow z-50 bg-white p-3"
                        >
                            <div className="flex flex-col gap-3">
                                <label
                                    htmlFor="detectionLimitFilter"
                                    className="text-sm font-bold text-neutral-700"
                                >
                                    Detection Limit (kg/h)
                                </label>
                                <input
                                    id="detectionLimitFilter"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={tempValue}
                                    onChange={(e) =>
                                        setTempValue(e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    onBlur={handleSave}
                                    className="px-3 py-2 text-sm border border-ae-gray-250 rounded focus:ring-2 focus:ring-ae-blue-550 focus:outline-none"
                                    placeholder="Enter limit (0 for all)"
                                    autoFocus
                                />
                                <p className="text-xs text-neutral-500">
                                    Show emissions with rate ≥ this value. Enter
                                    0 to show all emissions.
                                </p>
                            </div>
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};
